"use client";
import { useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";

interface Props {
  onTranscript: (text: string) => void;
  className?: string;
  iconSize?: string;
}

export default function MicButton({ onTranscript, className = "absolute right-2.5 bottom-2.5", iconSize = "w-4 h-4" }: Props) {
  const [listening, setListening] = useState(false);
  const recRef = useRef<any>(null);
  const debounceRef = useRef(0);

  const toggle = () => {
    const now = Date.now();
    if (now - debounceRef.current < 600) return;
    debounceRef.current = now;

    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { alert("Voice input is not supported in this browser. Try Chrome or Edge."); return; }

    if (listening) {
      if (recRef.current) recRef.current._userStopped = true;
      recRef.current?.stop();
      return;
    }

    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = true;
    rec._userStopped = false;
    recRef.current = rec;

    rec.onstart = () => setListening(true);
    rec.onresult = (e: any) => {
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript + " ";
      }
      if (final) onTranscript(final);
    };
    rec.onerror = (e: any) => {
      if (e.error === "not-allowed") alert("Microphone access was denied. Please allow microphone permission in your browser and try again.");
      setListening(false);
    };
    rec.onend = () => {
      if (!rec._userStopped) { try { rec.start(); } catch {} }
      else setListening(false);
    };
    rec.start();
  };

  return (
    <button
      type="button"
      onClick={toggle}
      title={listening ? "Stop recording" : "Voice input"}
      className={`${className} transition-all z-10 ${listening ? "!bg-red-500 !text-white scale-110" : ""}`}
    >
      {listening ? <MicOff className={iconSize} /> : <Mic className={iconSize} />}
    </button>
  );
}
